import { useState } from 'react';
import { Activity, Loader2, Search, Truck } from 'lucide-react';
import { shipmentServiceReal } from '../services/shipmentServiceReal';
import { driverActivityService } from '../services/driverActivityService';

type TimelineEvent = {
  key: string;
  label: string;
  recordedAt: string;
  source?: string | null;
  locationDescription?: string | null;
};

export function ShipmentTimelinePanel() {
  const [shipmentId, setShipmentId] = useState('');
  const [driverId, setDriverId] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [timeline, setTimeline] = useState<any | null>(null);
  const [activities, setActivities] = useState<any[]>([]);

  const loadByShipment = async () => {
    if (!shipmentId.trim()) return;
    setLoading(true);
    setError('');
    try {
      const response: any = await shipmentServiceReal.getShipmentTimeline(shipmentId.trim());
      const nextTimeline = response?.data || null;
      setTimeline(nextTimeline);
      if (nextTimeline?.shipmentId) {
        const rows = await driverActivityService.getByShipment(Number(nextTimeline.shipmentId));
        setActivities(Array.isArray(rows) ? rows : []);
      } else {
        setActivities([]);
      }
    } catch (err: any) {
      console.error('Shipment timeline load failed:', err);
      setError(err?.message || 'Timeline yüklenemedi');
      setTimeline(null);
      setActivities([]);
    } finally {
      setLoading(false);
    }
  };

  const loadByDriver = async () => {
    if (!driverId.trim()) return;
    setLoading(true);
    setError('');
    try {
      const response: any = await shipmentServiceReal.getDriverActiveShipmentTimeline(driverId.trim());
      const nextTimeline = response?.data || null;
      setTimeline(nextTimeline);
      if (nextTimeline?.shipmentId) {
        const rows = await driverActivityService.getByShipment(Number(nextTimeline.shipmentId));
        setActivities(Array.isArray(rows) ? rows : []);
      } else {
        setActivities([]);
      }
    } catch (err: any) {
      console.error('Driver active timeline load failed:', err);
      setError(err?.message || 'Timeline yüklenemedi');
      setTimeline(null);
      setActivities([]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6 p-6">
      <div>
        <h1 className="text-3xl font-bold text-slate-900">Sevkiyat Timeline</h1>
        <p className="mt-1 text-sm text-slate-500">Ozmal icin Seyir, spot icin mobil konum loglarindan turetilen asama zamani ve kalici event kayitlari.</p>
      </div>

      <div className="grid gap-4 rounded-2xl border border-slate-200 bg-white p-5 lg:grid-cols-2">
        <div className="space-y-3">
          <div className="text-sm font-semibold text-slate-800">Sevkiyat No / ID</div>
          <div className="flex gap-2">
            <input value={shipmentId} onChange={(e) => setShipmentId(e.target.value)} placeholder="Shipment ID" className="w-full rounded-xl border border-slate-300 px-3 py-2" />
            <button type="button" onClick={() => void loadByShipment()} disabled={loading || !shipmentId.trim()} className="inline-flex items-center gap-2 rounded-xl bg-blue-600 px-4 py-2 text-white disabled:opacity-50"><Search className="h-4 w-4" />Getir</button>
          </div>
        </div>
        <div className="space-y-3">
          <div className="text-sm font-semibold text-slate-800">Surucu ID</div>
          <div className="flex gap-2">
            <input value={driverId} onChange={(e) => setDriverId(e.target.value)} placeholder="Driver ID" className="w-full rounded-xl border border-slate-300 px-3 py-2" />
            <button type="button" onClick={() => void loadByDriver()} disabled={loading || !driverId.trim()} className="inline-flex items-center gap-2 rounded-xl bg-emerald-600 px-4 py-2 text-white disabled:opacity-50"><Truck className="h-4 w-4" />Aktif Son Sevkiyat</button>
          </div>
        </div>
      </div>

      {error ? <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">{error}</div> : null}
      {loading ? <div className="rounded-xl border border-slate-200 bg-white px-4 py-10 text-center text-slate-500"><Loader2 className="mx-auto mb-2 h-6 w-6 animate-spin text-blue-600" />Yukleniyor...</div> : null}

      {timeline ? (
        <>
          <div className="grid gap-4 rounded-2xl border border-slate-200 bg-white p-5 md:grid-cols-4">
            <div><div className="text-xs uppercase tracking-wide text-slate-500">Sevkiyat</div><div className="mt-1 font-semibold text-slate-900">{timeline.shipmentNo || timeline.shipmentId}</div></div>
            <div><div className="text-xs uppercase tracking-wide text-slate-500">Surucu</div><div className="mt-1 font-semibold text-slate-900">{timeline.driverName || '-'}</div></div>
            <div><div className="text-xs uppercase tracking-wide text-slate-500">Kaynak</div><div className="mt-1 font-semibold text-slate-900">{timeline.source || '-'}</div></div>
            <div><div className="text-xs uppercase tracking-wide text-slate-500">Tolerans</div><div className="mt-1 font-semibold text-slate-900">Varis {timeline.settings?.arrivalRadiusKm || '-'} km / Ayrilis {timeline.settings?.departureRadiusKm || '-'} km</div></div>
          </div>

          <div className="grid gap-6 lg:grid-cols-2">
            <div className="rounded-2xl border border-slate-200 bg-white p-5">
              <div className="mb-4 flex items-center gap-2 text-sm font-semibold text-slate-800"><Activity className="h-4 w-4 text-blue-600" />Turetilen Timeline</div>
              <div className="space-y-3">
                {(timeline.events as TimelineEvent[]).length === 0 ? <div className="text-sm text-slate-400">Event bulunamadi.</div> : (timeline.events as TimelineEvent[]).map((event) => (
                  <div key={event.key} className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
                    <div className="flex items-center justify-between gap-3">
                      <div className="font-medium text-slate-900">{event.label}</div>
                      <div className="text-xs text-slate-500">{event.recordedAt ? new Date(event.recordedAt).toLocaleString('tr-TR') : '-'}</div>
                    </div>
                    <div className="mt-1 text-xs text-slate-500">{event.locationDescription || '-'}{event.source ? ` • ${event.source}` : ''}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white p-5">
              <div className="mb-4 flex items-center gap-2 text-sm font-semibold text-slate-800"><Activity className="h-4 w-4 text-emerald-600" />Kalici Aktivite Loglari</div>
              <div className="space-y-3">
                {activities.length === 0 ? <div className="text-sm text-slate-400">Kayitli aktivite bulunamadi.</div> : activities.map((item) => (
                  <div key={item.id} className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
                    <div className="flex items-center justify-between gap-3">
                      <div className="font-medium text-slate-900">{item.action_label || item.stage_key || item.action_type}</div>
                      <div className="text-xs text-slate-500">{item.event_at ? new Date(item.event_at).toLocaleString('tr-TR') : new Date(item.created_at).toLocaleString('tr-TR')}</div>
                    </div>
                    <div className="mt-1 text-xs text-slate-500">{item.description || '-'}{item.vehicle_plate ? ` • ${item.vehicle_plate}` : ''}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
}
